import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import {
  newPasswordRule,
  passwordMatchValidator,
  PasswordRule,
  passwordRules,
} from '../../../utils/helpers';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AuthService } from '../../../services/auth/auth.service';
import { RuntimeConfigService } from '../../../services/common/runtime-config.service';
import { ResetPasswordRequest } from '../../../../types/reset-password';
import { CrossTabAuthSyncService } from '../../../services/auth/cross-tab-auth-sync.service';
import { normalizeApiErrors } from '../../../utils/api-errors.util';
import { isInvalidTokenFailure } from '../../../utils/reset-password-outcome.util';
import { filter, finalize, tap } from 'rxjs';

@Component({
  selector: 'reset-password',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    SkeletonModule,
    InputTextModule,
    PasswordModule,
    RouterLink,
  ],
  templateUrl: './reset-password.component.html',
})
export class ResetPasswordComponent {
  isLoadingYYCLogo: boolean = true;
  isSubmitting: boolean = false;
  passwordRules = signal<PasswordRule[]>([]);

  authService = inject(AuthService);
  route = inject(ActivatedRoute);
  router = inject(Router);
  // Gates the autocomplete tokens emitted by the password inputs.
  // See RuntimeConfig.authAutocompleteEnabled for the developer-note context.
  private runtimeConfig = inject(RuntimeConfigService);
  autocompleteEnabled = this.runtimeConfig.authAutocompleteEnabled;
  private forceFailure = this.runtimeConfig.forceResetPasswordFailure;

  private crossTabAuth = inject(CrossTabAuthSyncService);
  private destroyRef = inject(DestroyRef);

  constructor() {
    this.passwordRules.set(passwordRules);
    this.subscribeToSiblingResetOutcome();
  }

  /**
   * Cross-tab follow: when a sibling tab on the SAME reset link (matched by
   * email) submits the form, this tab moves to the same result page instead of
   * sitting on a form whose single-use token is already spent. The outcome is
   * rendered from navigation state exactly as for a local submit.
   */
  private subscribeToSiblingResetOutcome(): void {
    this.crossTabAuth.passwordReset$
      .pipe(
        filter((message) => {
          const ownEmail = this.route.snapshot.queryParamMap.get('email');
          const siblingEmail = message.passwordReset?.email;
          return (
            message.scope === 'customer' &&
            !!ownEmail &&
            !!siblingEmail &&
            ownEmail.toLowerCase() === siblingEmail.toLowerCase()
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((message) => {
        const outcome = message.passwordReset!;
        this.router.navigate(['/auth/reset-password-status'], {
          state: outcome.status
            ? { status: true }
            : { status: false, message: outcome.errors },
        });
      });
  }

  /**
   * Share this submit's outcome with sibling tabs on the same reset link — but
   * only when there is nothing left for them to try. P.O. (2026-10-05): a
   * "Failed" result (e.g. a password-policy rejection) must NOT move the other
   * tabs, so the user can try again from one of them. "Success" and "Expired"
   * both spend the single-use token, so those are shared.
   */
  private broadcastOutcomeToSiblings(email: string, status: boolean, rawErrors: unknown): void {
    const errors = normalizeApiErrors(rawErrors);
    if (!status && !isInvalidTokenFailure(errors)) return;

    this.crossTabAuth.broadcastPasswordReset({ email, status, errors });
  }

  newPasswordForm = new FormGroup(
    {
      inputNewPassword: new FormControl('', [
        Validators.required,
        newPasswordRule,
      ]),
      inputConfirmPassword: new FormControl('', Validators.required),
    },
    {
      validators: [passwordMatchValidator],
    }
  );

  get inputNewPassword() {
    return this.newPasswordForm.get('inputNewPassword');
  }

  get inputConfirmPassword() {
    return this.newPasswordForm.get('inputConfirmPassword');
  }

  isButtonDisabled(): boolean {
    return this.newPasswordForm.invalid || this.isSubmitting;
  }

  submitForm() {
    // Re-entrancy guard. `isButtonDisabled()` already reads `isSubmitting`, but
    // a disabled button only takes effect after a change-detection pass, so two
    // fast Enter presses used to emit two POSTs. That matters here because the
    // backend reset token is SINGLE-USE: a successful reset rotates the Identity
    // SecurityStamp, so the second request answers 400 and its navigation
    // overwrites the first one's success page with a failure screen.
    if (this.isSubmitting) return;

    if (this.newPasswordForm.invalid) {
      this.newPasswordForm.markAllAsTouched();
      return;
    }

    // QA / demo switch: show the Failed screen WITHOUT calling the backend, so
    // the password stays unchanged and no success email goes out. Forces the
    // generic Failed copy (no token-phrase message). Always false in production
    // builds — see RuntimeConfig.forceResetPasswordFailure.
    if (this.forceFailure()) {
      this.router.navigate(['/auth/reset-password-status'], {
        state: { status: false, message: [] },
      });
      return;
    }

    this.isSubmitting = true;

    const email = this.route.snapshot.queryParamMap.get('email');
    const token = this.route.snapshot.queryParamMap.get('token');
    const newPassword = this.inputNewPassword?.value;
    const confirmPassword = this.inputConfirmPassword?.value;

    if (!email || !token || !newPassword || !confirmPassword) {
      this.isSubmitting = false;
      return;
    }

    const credentials: ResetPasswordRequest = {
      email: email,
      token: token,
      newPassword: newPassword,
      confirmPassword: confirmPassword,
    };

    this.isSubmitting = true;

    this.authService
      .resetPassword(credentials)
      .pipe(
        tap({
          next: (response) => {
            this.broadcastOutcomeToSiblings(email, response.status === true, response.errors);
            if (response.status === true) {
              this.router.navigate(['/auth/reset-password-status'], {
                state: { status: response.status },
              });
            }
            else {
              this.router.navigate(['/auth/reset-password-status'], {
                state: { status: response.status, message: response.errors },
              });
            }
          },
        }),
        finalize(() => {
          this.isSubmitting = false;
        })
      )
      .subscribe();
  }
}
